import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, TrendingDown, DollarSign, ShoppingCart, Package, 
  Truck, Users, BarChart3, PieChart, Calendar, RefreshCw, Download
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  LineChart, Line, BarChart, Bar, PieChart as RechartPie, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Area, AreaChart
} from 'recharts';
import apiClient from '../lib/api';

import { MainLayout } from '../components/Layout';
const api = {
  get: async (url) => {
    const res = await apiClient.get(url);
    return res.data;
  }
};

const COLORS = ['#ef4444', '#f97316', '#eab308', '#22c55e', '#3b82f6', '#8b5cf6', '#ec4899'];

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
};

const formatNumber = (value) => {
  return new Intl.NumberFormat('pt-BR').format(value || 0);
};

// ==================== COMPONENTS ====================

const KPICard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = 'red' }) => {
  const colorClasses = {
    red: 'bg-red-100 text-red-600',
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    orange: 'bg-orange-100 text-orange-600',
    purple: 'bg-purple-100 text-purple-600'
  };
  
  return (
    <div className="bg-white rounded-xl border p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && <p className="text-xs text-gray-400 mt-1">{subtitle}</p>}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 mt-3 text-sm ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {trend >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
          <span>{Math.abs(trend).toFixed(1)}% vs período anterior</span>
        </div>
      )}
    </div>
  );
};

const TabButton = ({ active, onClick, icon: Icon, label }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-all ${
      active 
        ? 'border-red-500 text-red-600 bg-red-50' 
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
    }`}
  >
    <Icon className="w-4 h-4" />
    <span className="font-medium">{label}</span>
  </button>
);

const PeriodSelector = ({ value, onChange }) => (
  <select
    value={value}
    onChange={(e) => onChange(Number(e.target.value))}
    className="px-3 py-2 border rounded-lg text-sm"
  >
    <option value={7}>Últimos 7 dias</option>
    <option value={15}>Últimos 15 dias</option>
    <option value={30}>Últimos 30 dias</option>
    <option value={60}>Últimos 60 dias</option>
    <option value={90}>Últimos 90 dias</option>
    <option value={180}>Últimos 6 meses</option>
    <option value={365}>Último ano</option>
  </select>
);

// ==================== SALES TAB ====================

const SalesTab = ({ days }) => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState({});
  const [byChannel, setByChannel] = useState([]);
  const [byPeriod, setByPeriod] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [byHour, setByHour] = useState([]);

  useEffect(() => {
    loadData();
  }, [days]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [overviewData, channelData, periodData, productsData, hourData] = await Promise.all([
        api.get(`/analytics/sales/overview?days=${days}`),
        api.get(`/analytics/sales/by-channel?days=${days}`),
        api.get(`/analytics/sales/by-period?days=${days}&group_by=day`),
        api.get(`/analytics/sales/top-products?days=${days}&limit=10`),
        api.get(`/analytics/sales/by-hour?days=${days}`)
      ]);
      setOverview(overviewData);
      setByChannel(channelData.channels || []);
      setByPeriod(periodData.data || []);
      setTopProducts(productsData.products || []);
      setByHour(hourData.data || []);
    } catch (error) {
      toast.error('Erro ao carregar dados de vendas');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPICard
          title="Receita Total"
          value={formatCurrency(overview.total_revenue)}
          icon={DollarSign}
          trend={overview.revenue_growth}
          color="green"
        />
        <KPICard
          title="Total de Pedidos"
          value={formatNumber(overview.total_orders)}
          icon={ShoppingCart}
          trend={overview.orders_growth}
          color="blue"
        />
        <KPICard
          title="Ticket Médio"
          value={formatCurrency(overview.avg_ticket)}
          icon={TrendingUp}
          color="orange"
        />
        <KPICard
          title="Pedidos/Dia"
          value={formatNumber(Math.round(overview.total_orders / days))}
          subtitle={`média em ${days} dias`}
          icon={BarChart3}
          color="purple"
        />
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trend */}
        <div className="bg-white rounded-xl border p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Tendência de Receita</h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={byPeriod}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="period" tickFormatter={(v) => v ? new Date(v).toLocaleDateString('pt-BR', {day: '2-digit', month: '2-digit'}) : ''} />
              <YAxis tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => formatCurrency(v)} labelFormatter={(v) => new Date(v).toLocaleDateString('pt-BR')} />
              <Area type="monotone" dataKey="revenue" stroke="#ef4444" fillOpacity={1} fill="url(#colorRevenue)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Sales by Channel */}
        <div className="bg-white rounded-xl border p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Vendas por Canal</h3>
          <ResponsiveContainer width="100%" height={250}>
            <RechartPie>
              <Pie
                data={byChannel}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={5}
                dataKey="revenue"
                nameKey="channel"
                label={({ channel, percentage }) => `${channel}: ${percentage}%`}
              >
                {byChannel.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => formatCurrency(v)} />
            </RechartPie>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Products */}
        <div className="bg-white rounded-xl border p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Top 10 Produtos</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topProducts} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" tickFormatter={(v) => formatCurrency(v)} />
              <YAxis type="category" dataKey="name" width={120} tick={{fontSize: 12}} />
              <Tooltip formatter={(v) => formatCurrency(v)} />
              <Bar dataKey="revenue" fill="#ef4444" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Sales by Hour */}
        <div className="bg-white rounded-xl border p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Vendas por Hora</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={byHour}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="hour" tickFormatter={(v) => `${v}h`} />
              <YAxis tickFormatter={(v) => formatNumber(v)} />
              <Tooltip formatter={(v, name) => [formatNumber(v), name === 'orders' ? 'Pedidos' : 'Receita']} />
              <Bar dataKey="orders" fill="#3b82f6" name="Pedidos" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

// ==================== FINANCIAL TAB ====================

const FinancialTab = ({ days }) => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState({});
  const [cashflow, setCashflow] = useState([]);
  const [byCategory, setByCategory] = useState([]);

  useEffect(() => {
    loadData();
  }, [days]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [overviewData, cashflowData, categoryData] = await Promise.all([
        api.get(`/analytics/financial/overview?days=${days}`),
        api.get(`/analytics/financial/cashflow?days=${days}`),
        api.get(`/analytics/financial/by-category?days=${days}`)
      ]);
      setOverview(overviewData);
      setCashflow(cashflowData.data || []);
      setByCategory(categoryData.categories || []);
    } catch (error) {
      toast.error('Erro ao carregar dados financeiros');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPICard
          title="Receitas"
          value={formatCurrency(overview.total_income)}
          icon={TrendingUp}
          color="green"
        />
        <KPICard
          title="Despesas"
          value={formatCurrency(overview.total_expenses)}
          icon={TrendingDown}
          color="red"
        />
        <KPICard
          title="Lucro Líquido"
          value={formatCurrency(overview.net_profit)}
          subtitle={`Margem: ${overview.profit_margin}%`}
          icon={DollarSign}
          color="blue"
        />
        <KPICard
          title="A Receber"
          value={formatCurrency(overview.pending_receivables)}
          subtitle={`A Pagar: ${formatCurrency(overview.pending_payables)}`}
          icon={Calendar}
          color="orange"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Cash Flow */}
        <div className="bg-white rounded-xl border p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Fluxo de Caixa</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={cashflow}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" tickFormatter={(v) => new Date(v).toLocaleDateString('pt-BR', {day: '2-digit', month: '2-digit'})} />
              <YAxis tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => formatCurrency(v)} />
              <Legend />
              <Line type="monotone" dataKey="income" stroke="#22c55e" name="Receitas" strokeWidth={2} />
              <Line type="monotone" dataKey="expenses" stroke="#ef4444" name="Despesas" strokeWidth={2} />
              <Line type="monotone" dataKey="cumulative_balance" stroke="#3b82f6" name="Saldo Acumulado" strokeWidth={2} strokeDasharray="5 5" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Expenses by Category */}
        <div className="bg-white rounded-xl border p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Despesas por Categoria</h3>
          <ResponsiveContainer width="100%" height={300}>
            <RechartPie>
              <Pie
                data={byCategory}
                cx="50%"
                cy="50%"
                outerRadius={100}
                paddingAngle={2}
                dataKey="amount"
                nameKey="category"
                label={({ category, percentage }) => `${category}: ${percentage}%`}
              >
                {byCategory.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(v) => formatCurrency(v)} />
            </RechartPie>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

// ==================== STOCK TAB ====================

const StockTab = ({ days }) => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState({});
  const [movements, setMovements] = useState({});
  const [topMovers, setTopMovers] = useState([]);

  useEffect(() => {
    loadData();
  }, [days]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [overviewData, movementsData, moversData] = await Promise.all([
        api.get('/analytics/stock/overview'),
        api.get(`/analytics/stock/movements?days=${days}`),
        api.get(`/analytics/stock/top-movers?days=${days}&limit=10`)
      ]);
      setOverview(overviewData);
      setMovements(movementsData.movements || {});
      setTopMovers(moversData.products || []);
    } catch (error) {
      toast.error('Erro ao carregar dados de estoque');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPICard
          title="Total de Itens"
          value={formatNumber(overview.total_items)}
          icon={Package}
          color="blue"
        />
        <KPICard
          title="Valor em Estoque"
          value={formatCurrency(overview.total_value)}
          icon={DollarSign}
          color="green"
        />
        <KPICard
          title="Estoque Baixo"
          value={formatNumber(overview.low_stock_items)}
          icon={TrendingDown}
          color="orange"
        />
        <KPICard
          title="Sem Estoque"
          value={formatNumber(overview.out_of_stock_items)}
          icon={Package}
          color="red"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Movement Summary */}
        <div className="bg-white rounded-xl border p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Resumo de Movimentações</h3>
          <div className="space-y-4">
            {Object.entries(movements).map(([type, data]) => (
              <div key={type} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium">{type}</p>
                  <p className="text-sm text-gray-500">{data.count} movimentações</p>
                </div>
                <div className="text-right">
                  <p className="font-medium">{formatNumber(data.quantity)} un</p>
                  <p className="text-sm text-gray-500">{formatCurrency(data.value)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Top Movers */}
        <div className="bg-white rounded-xl border p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Produtos com Maior Movimentação</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={topMovers} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="name" width={100} tick={{fontSize: 11}} />
              <Tooltip />
              <Legend />
              <Bar dataKey="entries" fill="#22c55e" name="Entradas" stackId="a" />
              <Bar dataKey="exits" fill="#ef4444" name="Saídas" stackId="a" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

// ==================== DELIVERY TAB ====================

const DeliveryTab = ({ days }) => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState({});
  const [byArea, setByArea] = useState([]);

  useEffect(() => {
    loadData();
  }, [days]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [overviewData, areaData] = await Promise.all([
        api.get(`/analytics/delivery/overview?days=${days}`),
        api.get(`/analytics/delivery/by-area?days=${days}`)
      ]);
      setOverview(overviewData);
      setByArea(areaData.areas || []);
    } catch (error) {
      toast.error('Erro ao carregar dados de delivery');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="flex justify-center py-12"><RefreshCw className="w-6 h-6 animate-spin text-gray-400" /></div>;

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPICard
          title="Total de Entregas"
          value={formatNumber(overview.total_deliveries)}
          icon={Truck}
          color="blue"
        />
        <KPICard
          title="Taxa de Entrega"
          value={formatCurrency(overview.total_fees)}
          icon={DollarSign}
          color="green"
        />
        <KPICard
          title="Tempo Médio"
          value={`${Math.round(overview.avg_delivery_time || 0)} min`}
          icon={Calendar}
          color="orange"
        />
        <KPICard
          title="Entregas/Dia"
          value={formatNumber(Math.round(overview.total_deliveries / days))}
          icon={TrendingUp}
          color="purple"
        />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <div className="bg-white rounded-xl border p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Distribuição por Status</h3>
          <div className="space-y-3">
            {Object.entries(overview.by_status || {}).map(([status, count]) => {
              const total = overview.total_deliveries || 1;
              const percentage = Math.round((count / total) * 100);
              return (
                <div key={status} className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="font-medium">{status}</span>
                    <span className="text-gray-500">{count} ({percentage}%)</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-2">
                    <div 
                      className="bg-red-500 h-2 rounded-full" 
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Top Areas */}
        <div className="bg-white rounded-xl border p-5">
          <h3 className="font-semibold text-gray-900 mb-4">Top Bairros</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={byArea.slice(0, 10)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis type="number" />
              <YAxis type="category" dataKey="neighborhood" width={100} tick={{fontSize: 11}} />
              <Tooltip formatter={(v, name) => [name === 'deliveries' ? formatNumber(v) : `${Math.round(v)} min`, name === 'deliveries' ? 'Entregas' : 'Tempo Médio']} />
              <Bar dataKey="deliveries" fill="#3b82f6" name="Entregas" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

// ==================== MAIN PAGE ====================

export default function AnalyticsPage() {
  const [activeTab, setActiveTab] = useState('sales');
  const [days, setDays] = useState(30);
  const [dashboardData, setDashboardData] = useState(null);

  useEffect(() => {
    loadDashboard();
  }, [days]);

  const loadDashboard = async () => {
    try {
      const data = await api.get(`/analytics/dashboard?days=${days}`);
      setDashboardData(data);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    }
  };

  return (

    <MainLayout>
    <div className="space-y-6 overflow-hidden" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
          <p className="text-gray-500">Dashboards de análise do seu negócio</p>
        </div>
        <div className="flex items-center gap-3">
          <PeriodSelector value={days} onChange={setDays} />
          <Button variant="outline" onClick={loadDashboard}>
            <RefreshCw className="w-4 h-4 mr-2" /> Atualizar
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      {dashboardData && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-xl p-4 text-white">
            <p className="text-red-100 text-sm">Receita</p>
            <p className="text-xl font-bold truncate">{formatCurrency(dashboardData.sales?.total_revenue)}</p>
          </div>
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-4 text-white">
            <p className="text-blue-100 text-sm">Pedidos</p>
            <p className="text-xl font-bold truncate">{formatNumber(dashboardData.sales?.total_orders)}</p>
          </div>
          <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-4 text-white">
            <p className="text-green-100 text-sm">Lucro</p>
            <p className="text-xl font-bold truncate">{formatCurrency(dashboardData.financial?.net_profit)}</p>
          </div>
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl p-4 text-white">
            <p className="text-orange-100 text-sm">Ticket Médio</p>
            <p className="text-xl font-bold truncate">{formatCurrency(dashboardData.sales?.avg_ticket)}</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-xl border overflow-hidden">
        <div className="flex border-b overflow-x-auto">
          <TabButton active={activeTab === 'sales'} onClick={() => setActiveTab('sales')} icon={ShoppingCart} label="Vendas" />
          <TabButton active={activeTab === 'financial'} onClick={() => setActiveTab('financial')} icon={DollarSign} label="Financeiro" />
          <TabButton active={activeTab === 'stock'} onClick={() => setActiveTab('stock')} icon={Package} label="Estoque" />
          <TabButton active={activeTab === 'delivery'} onClick={() => setActiveTab('delivery')} icon={Truck} label="Delivery" />
        </div>

        <div className="p-6 overflow-x-auto">
          {activeTab === 'sales' && <SalesTab days={days} />}
          {activeTab === 'financial' && <FinancialTab days={days} />}
          {activeTab === 'stock' && <StockTab days={days} />}
          {activeTab === 'delivery' && <DeliveryTab days={days} />}
        </div>
      </div>
    </div>

    </MainLayout>

  );
}
